import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Grid,
  CircularProgress,
  Alert,
} from '@mui/material';
import { ArrowBack, Save } from '@mui/icons-material';
import axios from '../../config/axios';
import ReservableFormFields from '../../components/reservables/ReservableFormFields';
import ReservablePreviewCard from '../../components/reservables/ReservablePreviewCard';
import ModalResult from '../../components/ModalResult';
import {
  getInitialReservableForm,
  formToPayload,
  formToPreviewReservable,
  validateReservableForm,
} from '../../utils/reservableHelpers';

const ReservableCreate = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState(getInitialReservableForm);
  const [errors, setErrors] = useState({});
  const [teachers, setTeachers] = useState([]);
  const [loadingTeachers, setLoadingTeachers] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resultModal, setResultModal] = useState({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    const fetchTeachers = async () => {
      try {
        const response = await axios.get('/api/user');
        setTeachers(response.data.filter((u) => u.roles?.includes('teacher')));
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingTeachers(false);
      }
    };
    fetchTeachers();
  }, []);

  const preview = useMemo(
    () => formToPreviewReservable(form, teachers),
    [form, teachers]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateReservableForm(form);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    try {
      setSaving(true);
      const response = await axios.post('/api/reservables', formToPayload(form));
      navigate(`/manage-reservables/${response.data._id}`);
    } catch (err) {
      const message =
        err.response?.data?.error ||
        err.response?.data?.message ||
        'Errore durante la creazione della risorsa';
      setResultModal({ open: true, message, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate('/manage-reservables')}
        sx={{ mb: 2 }}
      >
        Torna all&apos;elenco
      </Button>

      <Typography variant="h4" component="h1" gutterBottom>
        Nuova risorsa
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Compila i campi e verifica l&apos;anteprima prima di salvare
      </Typography>

      {loadingTeachers && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Caricamento elenco istruttori...
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, lg: 8 }}>
            <Paper sx={{ p: 3 }}>
              <ReservableFormFields
                form={form}
                errors={errors}
                teachers={teachers}
                onChange={setForm}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
                <Button onClick={() => navigate('/manage-reservables')}>
                  Annulla
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <Save />}
                  disabled={saving}
                >
                  Crea risorsa
                </Button>
              </Box>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, lg: 4 }}>
            <Paper sx={{ p: 2, position: { lg: 'sticky' }, top: 88 }}>
              <ReservablePreviewCard reservable={preview} />
            </Paper>
          </Grid>
        </Grid>
      </Box>

      <ModalResult
        open={resultModal.open}
        onClose={() => setResultModal({ open: false, message: '', severity: 'info' })}
        message={resultModal.message}
        severity={resultModal.severity}
      />
    </Container>
  );
};

export default ReservableCreate;
